import React, { useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import StarRateOutlinedIcon from '@mui/icons-material/StarRateOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { Card, CardBody, StatusPill, TableScroll } from '../../PanelComerciales.styles';
import SectionHeader from '../SectionHeader/SectionHeader';
import ConfirmDialog from '../../modals/ConfirmDialog';
import AssignVipDrawer from '../../drawers/AssignVipDrawer';
import { Commercial, VipAccount } from '../../PanelComerciales.types';
import { panelTheme } from '../../PanelComerciales.theme';

interface VipTableProps {
    vips: VipAccount[];
    commercials: Commercial[];
    onAssign: (vip: VipAccount) => void;
    onRevoke: (vip: VipAccount) => void;
}

const VipTable: React.FC<VipTableProps> = ({ vips, commercials, onAssign, onRevoke }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [revoking, setRevoking] = useState<VipAccount | null>(null);

    return (
        <Card>
            <SectionHeader
                title='Cuentas VIP'
                icon={<StarRateOutlinedIcon style={{ color: '#D4A017' }} />}
                actions={
                    <Button
                        size='small'
                        variant='contained'
                        startIcon={<PersonAddAltOutlinedIcon />}
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                            textTransform: 'none',
                            background: panelTheme.colors.indigo,
                            '&:hover': { background: panelTheme.colors.violet },
                        }}
                    >
                        Asignar VIP
                    </Button>
                }
            />
            <CardBody>
                <TableScroll>
                    <table>
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Email</th>
                                <th>Comercial</th>
                                <th>Equipo</th>
                                <th>Motivo</th>
                                <th>Vigencia</th>
                                <th>Estado</th>
                                <th style={{ textAlign: 'right' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vips.map((v) => (
                                <tr key={v.id}>
                                    <td><strong>{v.client}</strong></td>
                                    <td>{v.email}</td>
                                    <td>{v.owner}</td>
                                    <td>{v.team}</td>
                                    <td>{v.reason}</td>
                                    <td>{v.validity}</td>
                                    <td>
                                        <StatusPill
                                            $variant={v.status === 'Activo' ? 'success' : 'muted'}
                                        >
                                            {v.status}
                                        </StatusPill>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Tooltip title='Revocar VIP'>
                                            <IconButton size='small' onClick={() => setRevoking(v)}>
                                                <DeleteOutlineOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                            {vips.length === 0 && (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
                                        No hay cuentas VIP activas.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </TableScroll>
            </CardBody>

            <AssignVipDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                commercials={commercials}
                existingEmails={vips.map((v) => v.email)}
                onSave={(vip) => {
                    onAssign(vip);
                    setDrawerOpen(false);
                }}
            />

            <ConfirmDialog
                open={Boolean(revoking)}
                title={`Revocar VIP · ${revoking?.client ?? ''}`}
                description='La cuenta volverá al ruteo automático estándar.'
                danger
                confirmLabel='Revocar'
                onCancel={() => setRevoking(null)}
                onConfirm={() => {
                    if (revoking) onRevoke(revoking);
                    setRevoking(null);
                }}
            />
        </Card>
    );
};

export default VipTable;
